import React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";

import Container from "@mui/material/Container";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import Alert from "../Alert";
import LoadingSpinner from "../../../components/LoadingSpinner";

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"COPYRIGHT © "} {new Date().getFullYear()}
      {" EVOKE MEDICAL CARE - ALL RIGHTS RESERVED"}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

export default function LoginForm(props) {
  const { onSubmitReset, alertMessage, passwordChanged } = props;
  const classes = useStyles();
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordTouched, setPasswordTouched] = React.useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] =
    React.useState(false);

  const [passwordLengthValid, setPasswordLengthValid] = React.useState(false);
  const [passwordValid, setPasswordValid] = React.useState(false);
  const [confirmPasswordValid, setConfirmPasswordValid] = React.useState(false);

  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] =
    React.useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    onSubmitReset(password);
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPasswordTouched(true);
    setPassword(val);
    var pattern = new RegExp(
      /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*].*$/
    );
    const isValid = pattern.test(val);
    if (isValid) {
      setPasswordValid(true);
    } else {
      setPasswordValid(false);
    }
    if (val.length < 12) {
      setPasswordLengthValid(false);
    } else {
      setPasswordLengthValid(true);
    }

    if (val !== confirmPassword) {
      setConfirmPasswordValid(false);
    } else {
      setConfirmPasswordValid(true);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmPasswordTouched(true);
    setConfirmPassword(val);

    if (val !== password) {
      setConfirmPasswordValid(false);
    } else {
      setConfirmPasswordValid(true);
    }
  };

  const togglePasswordVisible = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisible = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      {!passwordChanged && (
        <div className={classes.paper}>
          {props.loading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <LoadingSpinner />
              </div>
              <Typography component="h1" variant="h5">
                Resetting Password
              </Typography>
            </div>
          )}
          {!props.loading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              {/*<Avatar className={classes.avatar}>*/}
              {/*  <LockOutlinedIcon/>*/}
              {/*</Avatar>*/}
              <Typography component="h1" variant="h5">
                Reset Your Password
              </Typography>
            </div>
          )}
          <form
            onSubmit={onSubmit}
            className={classes.form}
            style={{ maxWidth: "480px" }}
            noValidate
          >
            <div style={{ position: "relative" }}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type={passwordVisible ? "text" : "password"}
                id="password"
                error={
                  (!passwordLengthValid || !passwordValid) && passwordTouched
                }
                autoComplete="current-password"
                onChange={handlePasswordChange}
                value={password}
              />
              {(!passwordLengthValid || !passwordValid) && passwordTouched && (
                <div style={{ color: "red", textAlign: "right" }}>
                  Error: Password must contain at least 12 characters including
                  one number and one special character.
                </div>
              )}
              {!passwordVisible && (
                <VisibilityIcon
                  onClick={togglePasswordVisible}
                  style={{
                    position: "absolute",
                    top: 33,
                    right: 17,
                    zIndex: "999",
                    cursor: "pointer",
                  }}
                />
              )}
              {passwordVisible && (
                <VisibilityOffIcon
                  onClick={togglePasswordVisible}
                  style={{
                    position: "absolute",
                    top: 33,
                    right: 17,
                    zIndex: "999",
                    cursor: "pointer",
                  }}
                />
              )}
            </div>
            <div style={{ position: "relative" }}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="confirm_password"
                label="Confirm New Password"
                type={confirmPasswordVisible ? "text" : "password"}
                id="confirm_password"
                error={!confirmPasswordValid && confirmPasswordTouched}
                onChange={handleConfirmPasswordChange}
                value={confirmPassword}
              />
              {!confirmPasswordValid && confirmPasswordTouched && (
                <div style={{ color: "red", textAlign: "right" }}>
                  Error: Passwords must match
                </div>
              )}
              {!confirmPasswordVisible && (
                <VisibilityIcon
                  onClick={toggleConfirmPasswordVisible}
                  style={{
                    position: "absolute",
                    top: 33,
                    right: 17,
                    zIndex: "999",
                    cursor: "pointer",
                  }}
                />
              )}
              {confirmPasswordVisible && (
                <VisibilityOffIcon
                  onClick={toggleConfirmPasswordVisible}
                  style={{
                    position: "absolute",
                    top: 33,
                    right: 17,
                    zIndex: "999",
                    cursor: "pointer",
                  }}
                />
              )}
            </div>
            {alertMessage && (
              <div style={{ color: "red", textAlign: "center" }}>
                <Alert message={alertMessage} />
              </div>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={
                !confirmPasswordValid || !passwordValid || !passwordLengthValid
              }
            >
              Reset Password
            </Button>
          </form>
        </div>
      )}
      {passwordChanged && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            marginTop: 200,
          }}
        >
          <Typography component="h1" variant="h5">
            Password Reset. Redirecting to Login Page...
          </Typography>
        </div>
      )}
      <Box mt={4}>
        <Copyright />
      </Box>
    </Container>
  );
}
